/**
 * "Read a supplier's quote onto this order."
 *
 * Upload the PDF the supplier emailed and the lines appear, matched to the
 * catalogue where the match is beyond doubt and left for a person where it is
 * not. Jonathon, 2026-09-01: "it reads the document and enters the line items
 * with their quantity and price. that will help streamline entering in material
 * orders."
 *
 * **Nothing is written until Add is pressed.** The whole screen is a proposal.
 * That matters more here than on most screens: an order is money, the reader is
 * a language model, and the cost of a wrong line is not a wrong pixel but a
 * pallet of the wrong board on a roof.
 *
 * Three things are always visible, because each is a way this goes wrong
 * quietly:
 *
 *   1. **The lines against the printed total.** If they disagree, the reader
 *      missed a line or misread a number, and the paper is right there to check
 *      against. This is the single most useful check on the screen and it takes
 *      a second.
 *   2. **Which lines are not linked to a product.** They are still ordered and
 *      still cost the right money — they just are not catalogue lines. A
 *      delivery charge belongs in that state permanently.
 *   3. **What the reader was unsure of**, in its own words.
 */

import {
  catalogueShortlist,
  planImport,
  withSupplierMatches,
  type CatalogueProduct,
  type MatchConfidence,
  type MatchedLine,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import {
  matchSupplierLines,
  readSupplierQuote,
  type ReadQuote,
} from "../../lib/supplierQuotes";

/**
 * Word overlap's matches, with Jev's choices applied where it was sure enough
 * (ADR 0155). A failure is not an error on this screen: the lines are still
 * read and still offered, just without Jev's help, and the note says why.
 */
async function withJev(
  matched: readonly MatchedLine[],
  products: readonly CatalogueProduct[],
): Promise<{ matched: MatchedLine[]; note: string | null }> {
  const lines = matched.map((line, i) => ({
    key: String(i),
    description: line.description,
    unit: line.unit ?? null,
  }));
  const options: Record<string, string[]> = {};
  for (const line of lines) {
    options[line.key] = catalogueShortlist(line.description, products).map(
      (p) => p.id,
    );
  }
  if (Object.values(options).every((ids) => ids.length === 0)) {
    return { matched: [...matched], note: null };
  }
  try {
    const answers = await matchSupplierLines({ lines, options });
    return {
      matched: withSupplierMatches(matched, answers, products),
      note: null,
    };
  } catch (e) {
    return {
      matched: [...matched],
      note: t("Matched by wording only: {error}", {
        error: e instanceof Error ? e.message : String(e),
      }),
    };
  }
}

/** What a line will become, once a person has had their say about it. */
interface Row {
  description: string;
  qty: number;
  unit: string | null;
  unitPrice: number;
  /** null means "order it, but do not link it to the catalogue". */
  productId: string | null;
  confidence: MatchConfidence;
  why: string;
  /** Products worth offering, best first. */
  choices: readonly CatalogueProduct[];
  include: boolean;
}

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export function ImportQuoteModal({
  products,
  onClose,
  onAdd,
}: {
  products: readonly CatalogueProduct[];
  onClose: () => void;
  onAdd: (
    rows: readonly {
      product_id: string | null;
      name: string;
      qty: number;
      unit: string | null;
      unit_price: number;
    }[],
  ) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<ReadQuote | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  /** Why Jev did not help, when it did not. */
  const [jevNote, setJevNote] = useState<string | null>(null);

  const read = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const answer = await readSupplierQuote(file);
      const plan = planImport(answer.lines, products);
      const jev = await withJev(plan.matched, products);
      setJevNote(jev.note);
      setQuote(answer);
      setRows(
        jev.matched.map((line) => ({
          description: line.description,
          qty: line.qty,
          unit: line.unit ?? null,
          unitPrice: line.unitPrice,
          // Only a confident match is pre-linked. A "possible" one is offered
          // in the dropdown with nothing chosen, so saying yes is deliberate.
          productId: line.confidence === "confident" ? line.product!.id : null,
          confidence: line.confidence,
          why: line.why,
          choices: line.product
            ? [line.product, ...line.alternatives]
            : line.alternatives,
          include: true,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setQuote(null);
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const chosen = rows.filter((r) => r.include);
  const lineTotal = useMemo(
    () => chosen.reduce((sum, r) => sum + r.qty * r.unitPrice, 0),
    [chosen],
  );
  /*
   * A cent of slack, and no more.
   *
   * Rounding on a page of a dozen lines can leave a penny; anything larger is
   * a line the reader missed, and that is exactly what this is here to catch.
   */
  const totalsAgree =
    quote?.printedTotal == null ||
    Math.abs(lineTotal - quote.printedTotal) < 0.01;

  const patch = (i: number, change: Partial<Row>) =>
    setRows((was) => was.map((r, j) => (j === i ? { ...r, ...change } : r)));

  return (
    <Modal
      title={t("Read a supplier's quote")}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={busy || chosen.length === 0}
            onClick={() =>
              void (async () => {
                setBusy(true);
                try {
                  await onAdd(
                    chosen.map((r) => ({
                      product_id: r.productId,
                      // The supplier's wording, not the catalogue's. Their
                      // invoice will say this, and reconciling the two is the
                      // job this screen exists for.
                      name: r.description,
                      qty: r.qty,
                      unit: r.unit,
                      unit_price: r.unitPrice,
                    })),
                  );
                  onClose();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                  setBusy(false);
                }
              })()
            }
          >
            {chosen.length === 0
              ? t("Add lines")
              : tn(chosen.length, "Add 1 line", "Add {n} lines")}
          </button>
        </>
      }
    >
      {error && <div className="err">{error}</div>}
      {jevNote && <div className="na-hint">{jevNote}</div>}

      {!quote && (
        <div className="field">
          {/* Tied to the input by id rather than left as a sibling: a file
              picker with no accessible name is one a screen reader announces
              as "button", and this is the only control on the screen. */}
          <label htmlFor="quote-file">{t("Supplier’s quotation")}</label>
          <input
            id="quote-file"
            type="file"
            accept="application/pdf,image/*"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void read(file);
            }}
          />
          <p className="na-hint">
            {t(
              "A PDF or a photo. The lines are read out for you to check — nothing is added to the order until you press Add.",
            )}
          </p>
        </div>
      )}

      {busy && !quote && <p className="na-hint">{t("Reading the quote…")}</p>}

      {quote && (
        <>
          <div className="rowline" style={{ marginBottom: 8 }}>
            <span className="an">
              {quote.supplier ?? t("Supplier not named on the page")}
            </span>
            {quote.quotedOn && (
              <span className="na-hint">
                {t("quoted {date}", { date: quote.quotedOn })}
              </span>
            )}
            {quote.expiresOn && (
              <span className="na-hint">
                {t("expires {date}", { date: quote.expiresOn })}
              </span>
            )}
          </div>

          {/* The check worth making, made for you. */}
          {!totalsAgree && (
            <div className="config-warn" role="status">
              <strong>{t("These lines do not add up to the quote.")}</strong>{" "}
              {t(
                "The page says {printed} and these lines come to {lines}. A line was probably missed or misread — check against the paper before adding.",
                {
                  printed: money(quote.printedTotal!),
                  lines: money(lineTotal),
                },
              )}
            </div>
          )}

          {quote.notes && (
            <div className="config-warn" role="status">
              <strong>{t("The reader noted:")}</strong> {quote.notes}
            </div>
          )}

          <table className="rows">
            <thead>
              <tr>
                <th />
                <th>{t("On the quote")}</th>
                <th className="num">{t("Qty")}</th>
                <th className="num">{t("Each")}</th>
                <th>{t("Catalogue product")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={row.include ? undefined : "sub"}>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.include}
                      aria-label={t("Include {line}", {
                        line: row.description,
                      })}
                      onChange={(e) => patch(i, { include: e.target.checked })}
                    />
                  </td>
                  <td>
                    <div className="rowline">
                      <span className="an">{row.description}</span>
                      <span className="na-hint">{row.why}</span>
                    </div>
                  </td>
                  <td className="num">
                    <input
                      className="num"
                      inputMode="decimal"
                      value={row.qty}
                      aria-label={t("Quantity for {line}", {
                        line: row.description,
                      })}
                      onChange={(e) =>
                        patch(i, { qty: Number(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td className="num">
                    <input
                      className="num"
                      inputMode="decimal"
                      value={row.unitPrice}
                      aria-label={t("Price for {line}", {
                        line: row.description,
                      })}
                      onChange={(e) =>
                        patch(i, { unitPrice: Number(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={row.productId ?? ""}
                      aria-label={t("Product for {line}", {
                        line: row.description,
                      })}
                      onChange={(e) =>
                        patch(i, { productId: e.target.value || null })
                      }
                    >
                      {/* Not "none" — this is a real and often correct answer.
                          A delivery charge belongs here permanently. */}
                      <option value="">{t("Not a catalogue product")}</option>
                      {row.choices.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} />
                <td className="num">{money(lineTotal)}</td>
                <td className="na-hint">
                  {quote.printedTotal == null
                    ? t("no total printed on the page")
                    : t("page says {total}", {
                        total: money(quote.printedTotal),
                      })}
                </td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </Modal>
  );
}
